// Belege: "Foto- und Datei-Upload" (3 Schritte) und "Rechnungserkennung" als eigene, einfache Seiten. Es werden ausschliesslich die vorhandenen Endpunkte genutzt
// (Upload, lokale Texterkennung). Die Upload-Funktion in der Belegliste bleibt zusaetzlich bestehen.
import { useState, useEffect, useRef, useCallback } from "react";
import THEME from "../../../../theme";
import { fs } from "../../utils/styles";
import { belegeApi, belegeStufe, BELEGARTEN } from "../../../belege/belegeApi";
import { Chip, Klein, Meldung, euro, datum, useAktion } from "../../../belege/ui";
import { Seitenkopf, Abschnitt } from "../../ui/Bausteine";

// Upload in 3 Schritten: 1 Foto/Datei waehlen -> 2 Vorschau & Angaben pruefen -> 3 Speichern. Es wird erst in Schritt 3 etwas an den Server gesendet
// (vorhandener Endpunkt belegeApi.upload; die optionalen Angaben danach per belegeApi.aendern - dieselben Pruefungen wie in der Belegliste).
// Die Angaben sind freiwillig; in der erweiterten Stufe fuellt die Erkennung leere Felder nach dem Speichern vor.
const SCHRITTE = ["Foto oder Datei wählen", "Vorschau und Angaben prüfen", "Speichern"];
const LEER = { lieferant: "", rechnungsnr: "", rechnungsdatum: "", brutto: "", ust: "", belegart: "", notiz: "" };

function Vorschaubild({ datei }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    const u = URL.createObjectURL(datei);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [datei]);
  const art = datei.type || "";
  const rahmen = { width: "100%", height: 260, border: `1px solid ${THEME.colors.border}`, borderRadius: THEME.radius.md, background: "#fff", objectFit: "contain" };
  return (
    <figure style={{ margin: 0, minWidth: 0 }}>
      {url && art.startsWith("image/") && !/hei[cf]/i.test(art) && <img src={url} alt={`Vorschau ${datei.name}`} style={rahmen} />}
      {url && art === "application/pdf" && <iframe title={`Vorschau ${datei.name}`} src={url} style={rahmen} />}
      {(!art || (!art.startsWith("image/") && art !== "application/pdf") || /hei[cf]/i.test(art)) && (
        <div style={{ ...rahmen, display: "flex", alignItems: "center", justifyContent: "center", background: THEME.colors.surfaceMuted, color: THEME.colors.textSecondary, textAlign: "center", padding: 12 }}>
          Keine Vorschau im Browser möglich (z. B. iPhone-Foto HEIC) – die Datei wird trotzdem gespeichert.
        </div>
      )}
      <figcaption style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, marginTop: 4, overflowWrap: "anywhere" }}>{datei.name} · {(datei.size / 1024).toFixed(0)} KB</figcaption>
    </figure>
  );
}

export function BelegeUpload({ geheZu, vorgabe }) {
  const [schritt, setSchritt] = useState(1);
  const [dateien, setDateien] = useState([]);
  const [quelle, setQuelle] = useState("upload");
  const [felder, setFelder] = useState(LEER);
  const [fehler, setFehler] = useState("");
  const [hinweis, setHinweis] = useState("");
  const [ergebnisse, setErgebnisse] = useState([]);
  const [laed, setLaed] = useState(false);
  const [ueber, setUeber] = useState(false);
  const datei = useRef(null);
  const kamera = useRef(null);

  const hinzufuegen = useCallback((neue, q) => {
    const liste = Array.from(neue || []);
    if (!liste.length) return;
    setDateien(d => [...d, ...liste]);
    setQuelle(q);
    setFehler(""); setHinweis("");
    setSchritt(2);
  }, []);
  // Uebergabe von der Startseite ("Beleg fotografieren/hochladen"): Datei ist schon gewaehlt, weiter mit Schritt 2
  const verwendet = useRef(null);
  useEffect(() => {
    if (vorgabe && vorgabe.id !== verwendet.current) { verwendet.current = vorgabe.id; hinzufuegen(vorgabe.dateien, vorgabe.quelle); }
  }, [vorgabe, hinzufuegen]);

  const set = (k) => (e) => setFelder(f => ({ ...f, [k]: e.target.value }));
  const neu = () => { setDateien([]); setFelder(LEER); setSchritt(1); setFehler(""); setHinweis(""); setQuelle("upload"); };

  const speichern = async () => {
    if (!dateien.length) return;
    setFehler(""); setHinweis(""); setLaed(true); setSchritt(3);
    const fd = new FormData();
    dateien.forEach(f => fd.append("files", f, f.name));
    fd.append("quelle", quelle);
    let r;
    try {
      r = await belegeApi.upload(fd);
    } catch (e) { setFehler(e.message); setLaed(false); setSchritt(2); return; }
    const angaben = Object.fromEntries(Object.entries(felder).filter(([, v]) => String(v).trim() !== ""));
    let angabenFehler = "";
    if (!r.dublette && Object.keys(angaben).length) {
      try { await belegeApi.aendern(r.beleg_id, angaben); } catch (e) { angabenFehler = e.message; }
    }
    setErgebnisse(e => [{ ...r, zeit: new Date(), angabenFehler }, ...e]);
    if (r.dublette) setHinweis(`Bereits erfasst: ${r.beleg_nr}`);
    else if (angabenFehler) setFehler(`Beleg ${r.beleg_nr} ist gespeichert, die Angaben konnten aber nicht übernommen werden: ${angabenFehler} – bitte im Beleg nachtragen.`);
    else setHinweis(belegeStufe.erweitert ? `Gespeichert als ${r.beleg_nr} – die Erkennung füllt leere Felder gleich vor.` : `Gespeichert als ${r.beleg_nr}${Object.keys(angaben).length ? "" : " – bitte Rechnungsdatum, Lieferant, Betrag und Umsatzsteuer unter „Alle Belege“ eintragen"}.`);
    setLaed(false);
  };

  const letzter = ergebnisse[0];
  const stepper = (
    <ol aria-label="Schritte" style={{ display: "flex", gap: 8, listStyle: "none", margin: "0 0 16px", padding: 0, flexWrap: "wrap" }}>
      {SCHRITTE.map((n, i) => {
        const aktiv = schritt === i + 1; const fertig = schritt > i + 1;
        return (
          <li key={n} aria-current={aktiv ? "step" : undefined} style={{ display: "flex", alignItems: "center", gap: 8, flex: "1 1 150px", minWidth: 0, padding: "8px 12px", borderRadius: THEME.radius.md,
            border: `1px solid ${aktiv ? THEME.colors.primary : THEME.colors.border}`, background: aktiv ? THEME.colors.surfaceMuted : "transparent", color: aktiv ? THEME.colors.text : THEME.colors.textSecondary }}>
            <span style={{ fontWeight: 700, color: fertig ? THEME.colors.success : aktiv ? THEME.colors.primary : THEME.colors.textMuted }}>{fertig ? "✓" : i + 1}</span>
            <span style={{ fontSize: THEME.fontSize.sm, overflowWrap: "anywhere" }}>{n}</span>
          </li>
        );
      })}
    </ol>
  );

  return (
    <div style={{ color: THEME.colors.text }}>
      <Seitenkopf titel="Foto- und Datei-Upload" untertitel={"Rechnung fotografieren oder Datei hochladen (PDF, JPG, PNG, WebP" + (belegeStufe.heic ? ", iPhone-Foto HEIC" : "") + "; mehrere Seiten möglich). " + (belegeStufe.erweitert ? "Danach wird sie automatisch gelesen." : "Die Angaben werden anschließend von Hand erfasst (keine automatische Erkennung).")} />
      {stepper}
      <Meldung fehler={fehler} hinweis={hinweis} />

      <input ref={datei} type="file" multiple accept="image/*,.heic,.heif,application/pdf" style={{ display: "none" }} aria-label="Datei wählen" onChange={e => { hinzufuegen(e.target.files, "upload"); e.target.value = ""; }} />
      <input ref={kamera} type="file" accept="image/*" capture="environment" style={{ display: "none" }} aria-label="Foto aufnehmen" onChange={e => { hinzufuegen(e.target.files, "kamera"); e.target.value = ""; }} />

      {schritt === 1 && (
        <Abschnitt titel="1 · Foto oder Datei wählen">
          <div onDragOver={e => { e.preventDefault(); setUeber(true); }} onDragLeave={() => setUeber(false)} onDrop={e => { e.preventDefault(); setUeber(false); hinzufuegen(e.dataTransfer.files, "upload"); }}
            style={{ border: `2px dashed ${ueber ? THEME.colors.primary : THEME.colors.border}`, borderRadius: THEME.radius.lg, padding: "24px 16px", textAlign: "center" }}>
            <div style={{ marginBottom: 14, color: THEME.colors.textSecondary }}>Dateien hierher ziehen oder auswählen</div>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <button style={{ ...fs.btn, minHeight: 56, minWidth: 200 }} onClick={() => kamera.current?.click()}>📷 Foto aufnehmen</button>
              <button style={{ ...fs.btnGray, minHeight: 56, minWidth: 200 }} onClick={() => datei.current?.click()}>+ Datei auswählen</button>
            </div>
          </div>
          {belegeStufe.uploadZugang && <Klein>Uploads von Smartphones anderer Personen laufen über „Smartphone-Zugänge“ (eigener, auf Upload beschränkter Zugang).</Klein>}
        </Abschnitt>
      )}

      {schritt === 2 && (
        <Abschnitt titel="2 · Vorschau und Angaben prüfen">
          <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
            {dateien.map((d, i) => (
              <div key={`${d.name}-${i}`} style={{ minWidth: 0 }}>
                <Vorschaubild datei={d} />
                <button style={{ ...fs.btnDanger, minHeight: 48, marginTop: 6 }} onClick={() => { const rest = dateien.filter((_, j) => j !== i); setDateien(rest); if (!rest.length) setSchritt(1); }}>Entfernen</button>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "12px 0" }}>
            <button style={{ ...fs.btnGray, minHeight: 48 }} onClick={() => kamera.current?.click()}>📷 Weitere Seite fotografieren</button>
            <button style={{ ...fs.btnGray, minHeight: 48 }} onClick={() => datei.current?.click()}>+ Weitere Datei</button>
          </div>
          <Klein>Mehrere Dateien werden als ein Beleg (mehrseitig) gespeichert. Die Angaben sind freiwillig und lassen sich später im Beleg ergänzen oder ändern.</Klein>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginTop: 14 }}>
            <div><label style={fs.label} htmlFor="up-lieferant">Lieferant</label><input id="up-lieferant" style={{ ...fs.input, width: "100%" }} value={felder.lieferant} onChange={set("lieferant")} /></div>
            <div><label style={fs.label} htmlFor="up-nr">Rechnungsnr.</label><input id="up-nr" style={{ ...fs.input, width: "100%" }} value={felder.rechnungsnr} onChange={set("rechnungsnr")} /></div>
            <div><label style={fs.label} htmlFor="up-datum">Rechnungsdatum</label><input id="up-datum" type="date" style={{ ...fs.input, width: "100%" }} value={felder.rechnungsdatum} onChange={set("rechnungsdatum")} /></div>
            <div><label style={fs.label} htmlFor="up-brutto">Brutto (€)</label><input id="up-brutto" inputMode="decimal" style={{ ...fs.input, width: "100%" }} value={felder.brutto} onChange={set("brutto")} placeholder="z. B. 119,00" /></div>
            <div><label style={fs.label} htmlFor="up-ust">Umsatzsteuer (€)</label><input id="up-ust" inputMode="decimal" style={{ ...fs.input, width: "100%" }} value={felder.ust} onChange={set("ust")} placeholder="z. B. 19,00" /></div>
            <div><label style={fs.label} htmlFor="up-art">Belegart</label>
              <select id="up-art" style={{ ...fs.input, width: "100%" }} value={felder.belegart} onChange={set("belegart")}><option value="">–</option>{BELEGARTEN.map(([k, n]) => <option key={k} value={k}>{n}</option>)}</select></div>
          </div>
          <label style={{ ...fs.label, marginTop: 10 }} htmlFor="up-notiz">Notiz</label>
          <textarea id="up-notiz" rows={2} style={{ ...fs.input, width: "100%", resize: "vertical" }} value={felder.notiz} onChange={set("notiz")} />
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
            <button style={{ ...fs.btn, minHeight: 52, flex: "1 1 200px" }} disabled={laed || !dateien.length} onClick={speichern}>Speichern</button>
            <button style={{ ...fs.btnGray, minHeight: 52 }} onClick={neu}>Abbrechen</button>
          </div>
        </Abschnitt>
      )}

      {schritt === 3 && (
        <Abschnitt titel="3 · Speichern">
          {laed && <div role="status">Wird gespeichert…</div>}
          {!laed && letzter && (
            <div style={{ display: "grid", gap: 10 }}>
              <div><strong>{letzter.beleg_nr}</strong> {letzter.dublette ? <Chip text="bereits vorhanden" farbe={THEME.colors.warning} /> : <Chip text="neu erfasst" farbe={THEME.colors.success} />}</div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button style={{ ...fs.btn, minHeight: 52 }} onClick={() => geheZu("belege", { beleg: letzter.beleg_id })}>Beleg öffnen</button>
                <button style={{ ...fs.btnGray, minHeight: 52 }} onClick={neu}>Weiteren Beleg erfassen</button>
              </div>
            </div>
          )}
          {!laed && !letzter && <Klein>Nichts gespeichert.</Klein>}
        </Abschnitt>
      )}

      {ergebnisse.length > 1 && (
        <Abschnitt titel="Gerade hochgeladen">
          {ergebnisse.map((r, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center", padding: "8px 0", borderTop: `1px solid ${THEME.colors.border}` }}>
              <span><strong>{r.beleg_nr}</strong> {r.dublette ? <Chip text="bereits vorhanden" farbe={THEME.colors.warning} /> : <Chip text="neu erfasst" farbe={THEME.colors.success} />}</span>
              <button style={{ ...fs.btnGray, minHeight: 48 }} onClick={() => geheZu("belege", { beleg: r.beleg_id })}>Beleg öffnen</button>
            </div>
          ))}
        </Abschnitt>
      )}
    </div>
  );
}

export function BelegeErkennung({ geheZu }) {
  const [ocr, setOcr] = useState(null);
  const [liste, setListe] = useState(null);
  const [fehler, setFehler] = useState("");
  const [hinweis, setHinweis] = useState("");
  const aktion = useAktion(setFehler, setHinweis);
  const laden = useCallback(() => {
    belegeApi.ocrStatus().then(setOcr).catch(e => setFehler(e.message));
    belegeApi.liste({ status: "eingegangen", limit: 100 }).then(setListe).catch(e => setFehler(e.message));
  }, []);
  useEffect(() => { laden(); }, [laden]);
  const luecken = (b) => [!b.lieferant && "Lieferant", !b.rechnungsdatum && "Rechnungsdatum", (b.brutto === null || b.brutto === undefined) && "Betrag"].filter(Boolean);

  return (
    <div style={{ color: THEME.colors.text }}>
      <Seitenkopf titel="Rechnungserkennung" untertitel="Liest Lieferant, Rechnungsnummer, Datum und Beträge aus Belegen – lokal auf dem Server, ohne externe Dienste. Erkannte Werte sind Vorschläge und werden von dir geprüft."
        rechts={<button style={fs.btn} onClick={() => aktion(() => belegeApi.erkennungOffene(), r => `Erkennung: ${r.ok} von ${r.geprueft} Belegen gelesen${r.fehler ? `, ${r.fehler} Fehler` : ""}.`).then(laden)}>🔍 Neue Belege jetzt lesen</button>} />
      <Meldung fehler={fehler} hinweis={hinweis} />
      <Abschnitt titel="Stand der Texterkennung">
        {ocr ? (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Chip text={ocr.engine ? `OCR bereit (${ocr.engine})` : "OCR nicht installiert – nur PDFs mit Text werden gelesen"} farbe={ocr.engine ? THEME.colors.success : THEME.colors.warning} />
            <Chip text={ocr.automatisch ? "automatisch nach dem Upload" : "nur auf Knopfdruck"} farbe={THEME.colors.accent} />
          </div>
        ) : "Lädt…"}
      </Abschnitt>
      <Abschnitt titel={`Belege im Status „Eingegangen“${liste ? ` (${liste.gesamt})` : ""}`}>
        {liste && liste.belege.length === 0 && <div style={{ color: THEME.colors.success }}>Alle Belege wurden bereits geprüft.</div>}
        <div style={{ display: "grid", gap: 8 }}>
          {(liste?.belege || []).map(b => {
            const l = luecken(b);
            return (
              <div key={b.id} style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center", border: `1px solid ${THEME.colors.border}`, borderLeft: `4px solid ${l.length ? THEME.colors.warning : THEME.colors.success}`, borderRadius: THEME.radius.md, padding: "8px 12px" }}>
                <div style={{ minWidth: 180, flex: 1 }}>
                  <strong>{b.beleg_nr}</strong> · {b.lieferant || "Lieferant fehlt"}{b.rechnungsnr ? ` · ${b.rechnungsnr}` : ""}
                  <Klein>{datum(b.rechnungsdatum || b.erfasst_am)} · {euro(b.brutto)}{l.length ? ` · fehlt: ${l.join(", ")}` : " · Angaben vollständig – bitte prüfen"}</Klein>
                </div>
                <span style={{ display: "flex", gap: 6 }}>
                  <button style={fs.btnGray} onClick={() => aktion(() => belegeApi.erkennungStarten(b.id), "Erkennung ausgeführt.").then(laden)}>Erkennung starten</button>
                  <button style={fs.btn} onClick={() => geheZu("belege", { beleg: b.id })}>Prüfen</button>
                </span>
              </div>
            );
          })}
        </div>
      </Abschnitt>
    </div>
  );
}
